import { Config } from '@app/src/config'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import RequestError from '@app/src/errors/RequestError'
import sentryService from '@app/src/services/SentryService'
import type {
  ErrorResponse,
  HTTPMethod,
  MethodWithBodyAndMethodType,
  MethodWithBodyType,
  MethodWithoutBodyType,
  OptionsType,
  RequestParameters,
  RequestParametersWithBody,
  UseApiType,
} from '@app/src/types/api'
import { useCallback } from 'react'

const isFormData = (value: FormData | unknown): value is FormData => value instanceof FormData

const useApi = (): UseApiType => {
  const { getToken } = useAuthentication()

  const getHeaders = useCallback(
    async (headers?: RequestInit['headers'], skipContentTypeHeader?: boolean): Promise<RequestInit['headers']> => {
      return {
        Authorization: `Bearer ${await getToken()}`,
        ...(!skipContentTypeHeader ? { 'Content-Type': 'application/json;  charset=UTF-8' } : {}),
        ...(headers ?? {}),
      }
    },
    [getToken],
  )

  async function getBlobBody(response: Response) {
    const contentDispositionHeader = response.headers.get('content-disposition') ?? ''
    const pattern = new RegExp(/filename=(.*);/)
    const fileName = pattern.exec(contentDispositionHeader)?.[1] ?? 'File'
    return { fileName, blob: await response.blob() }
  }

  const getBody = async (response: Response, isBlob?: boolean): Promise<unknown> => {
    if (response.headers.get('Content-Length') === '0') return {}
    try {
      // A failed download still answers with a JSON error body, and parsing it as a blob
      // throws away the backend's sentryId along with the rest of it.
      if (isBlob && response.ok) {
        return await getBlobBody(response)
      }
      if (response.status === 204) {
        return
      }
      return await response.json()
    } catch (jsonError: unknown) {
      if (jsonError instanceof Error) {
        // The parser echoes the first bytes of the body in its message, which would become
        // both the issue title and its grouping key, so describe the response instead.
        const contentType = response.headers.get('content-type') ?? 'unknown'
        throw new RequestError(response.status, null, `Malformed response body (content-type: ${contentType})`)
      } else {
        throw jsonError
      }
    }
  }

  const request: MethodWithBodyAndMethodType = async <ResponseBody, RequestBody = ResponseBody>(
    method: HTTPMethod,
    requestParameters: RequestParametersWithBody<RequestBody>,
    options?: OptionsType,
  ): Promise<ResponseBody> => {
    // Query strings carry search terms and other customer data, and this url reaches Sentry
    // as both the exception message and the endpoint tag.
    const requestContext = { method: method.toUpperCase(), url: requestParameters.url.split('?')[0] }

    try {
      const url = `${Config.API_URL}${requestParameters.url}`

      const skipContentTypeHeader = isFormData(requestParameters.body)
      const body = isFormData(requestParameters.body) ? requestParameters.body : JSON.stringify(requestParameters.body)

      const response = await fetch(url, {
        method: method?.toUpperCase(),
        ...requestParameters.config,
        body,
        headers: await getHeaders(requestParameters.config?.headers, skipContentTypeHeader),
      })

      const responseBody = await getBody(response, options?.isBlob)
      if (!response.ok) {
        throw new RequestError(response.status, responseBody as ErrorResponse, undefined, requestContext)
      }
      return responseBody as ResponseBody
    } catch (error: unknown) {
      if (error instanceof RequestError) {
        sentryService.captureApiFailure({
          error,
          method: requestContext.method,
          endpoint: requestContext.url,
          statusCode: error.statusCode,
          backendSentryId: error.backendSentryId,
          errorCode: error.errorCode,
        })
      } else if (error instanceof Error) {
        // react-query consumes these rejections, so without this they reach neither the
        // global handler nor Sentry. Transport failures are dropped by the noise rules.
        sentryService.captureError(error, {
          tags: {
            errorType: 'api-client',
            method: requestContext.method,
            endpoint: requestContext.url,
          },
        })
      }
      throw error
    }
  }

  const get: MethodWithoutBodyType = async <ResponseBody>(
    requestParameters: RequestParameters,
    options?: OptionsType,
  ) => {
    return request<ResponseBody>('get', requestParameters, options)
  }

  const post: MethodWithBodyType = async <ResponseBody, RequestBody>(
    requestParameters: RequestParametersWithBody<RequestBody>,
    options?: OptionsType,
  ) => {
    return request<ResponseBody, RequestBody>('post', requestParameters, options)
  }

  const put: MethodWithBodyType = async <ResponseBody, RequestBody>(
    requestParameters: RequestParametersWithBody<RequestBody>,
  ) => {
    return request<ResponseBody, RequestBody>('put', requestParameters)
  }

  const patch: MethodWithBodyType = async <ResponseBody, RequestBody>(
    requestParameters: RequestParametersWithBody<RequestBody>,
  ) => {
    return request<ResponseBody, RequestBody>('patch', requestParameters)
  }

  const deleteMethod: MethodWithBodyType = async <ResponseBody, RequestBody>(
    requestParameters: RequestParametersWithBody<RequestBody>,
  ) => {
    return request<ResponseBody, RequestBody>('delete', requestParameters)
  }

  return {
    request,
    get,
    post,
    put,
    delete: deleteMethod,
    patch,
  }
}

export default useApi
